import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { FORBIDDEN_MESSAGE } from '../utils/constants';
import { hasHandoffCode, takeHandoffCode, exchangeHandoffCode } from '../utils/handoff';

const AuthContext = createContext(null);

// Role only: designation is a job title tutors can set on their own profile.
const checkIsSuperAdmin = (user) => user?.role === 'admin';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('careers_token');
    localStorage.removeItem('careers_refresh_token');
    localStorage.removeItem('careers_user');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Check token on mount
  useEffect(() => {
    const token = localStorage.getItem('careers_token');
    // Opened from Control Centre with #handoff=: the effect below signs in.
    if (!token && hasHandoffCode()) return;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          clearAuth();
        } else {
          let stored = null;
          try {
            const raw = localStorage.getItem('careers_user');
            if (raw) stored = JSON.parse(raw);
          } catch (_) {}
          const profile = stored?.user ?? stored?.data?.user ?? stored?.data ?? stored;
          setUser({ ...decoded, ...(profile && typeof profile === 'object' ? profile : {}) });
          setIsAuthenticated(true);
        }
      } catch {
        clearAuth();
      }
    } else {
      setIsAuthenticated(false);
    }
  }, [clearAuth]);

  // Auto-logout on any 401 response from the API. A 403 is a refusal, not an
  // expired session: keep the user signed in and give every error display the
  // same plain message.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) clearAuth();
        if (err.response?.status === 403 && err.response.data && typeof err.response.data === 'object') {
          err.response.data.message = FORBIDDEN_MESSAGE;
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [clearAuth]);

  const login = useCallback(({ token, refreshToken, user: userData, data: responseData }) => {
    localStorage.setItem('careers_token', token);
    if (refreshToken) localStorage.setItem('careers_refresh_token', refreshToken);
    const toStore = userData ?? responseData ?? {};
    if (Object.keys(toStore).length) localStorage.setItem('careers_user', JSON.stringify(toStore));
    const decoded = jwtDecode(token);
    const profile = userData ?? responseData?.user ?? responseData?.data ?? responseData;
    setUser({ ...decoded, ...(profile && typeof profile === 'object' ? profile : {}) });
    setIsAuthenticated(true);
  }, []);

  // Swap a one-time code from Control Centre for a session. Left "checking"
  // (null) until it settles so the login page doesn't flash first.
  useEffect(() => {
    const code = takeHandoffCode();
    if (!code || localStorage.getItem('careers_token')) return;
    exchangeHandoffCode(code)
      .then(login)
      .catch(() => setIsAuthenticated(false));
  }, [login]);

  const logout = useCallback(() => {
    clearAuth();
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...prev, ...updates };
      // Persist back to localStorage so it survives a refresh
      try {
        const raw = localStorage.getItem('careers_user');
        const stored = raw ? JSON.parse(raw) : {};
        const profile = stored?.user ?? stored?.data?.user ?? stored?.data ?? stored;
        const updated = { ...profile, ...updates };
        // Mirror the shape that was originally stored
        if (stored?.user) localStorage.setItem('careers_user', JSON.stringify({ ...stored, user: updated }));
        else localStorage.setItem('careers_user', JSON.stringify(updated));
      } catch (_) {}
      return next;
    });
  }, []);

  const isSuperAdmin = checkIsSuperAdmin(user);
  const isClubAdmin  = user?.role === 'clubAdmin';
  const isSponsor    = user?.role === 'sponsor';
  const isTutor      = user?.role === 'tutor';

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      login,
      logout,
      updateUser,
      isSuperAdmin,
      isClubAdmin,
      isSponsor,
      isTutor,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
